import i18n from "../i18n";

const MUSCAT = "Asia/Muscat";

/** Arabic month names and phrasing, but Western digits: invoice figures and ids stay 0-9. */
const AR = "ar-u-nu-latn";
const isArabic = (): boolean => i18n.language === "ar";

/** OMR amount, three decimals, en-US grouping, minus sign U+2212. */
export function omr(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  const sign = n < 0 ? "−" : "";
  return sign + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function pct(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toFixed(1) + "%";
}

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  timeZone: MUSCAT,
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};
const dateTimeFormatter = new Intl.DateTimeFormat("en-GB", dateTimeOptions);
const dateTimeFormatterAr = new Intl.DateTimeFormat(AR, dateTimeOptions);

/** "18 Aug 2026 09:14" in Asia/Muscat. */
export function dateTime(iso: string): string {
  const parts = (isArabic() ? dateTimeFormatterAr : dateTimeFormatter).formatToParts(new Date(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Compact elapsed time for a table column: "45m", "2h", "3d". Never negative. */
export function age(iso: string, now: Date = new Date()): string {
  const diff = Math.max(now.getTime() - new Date(iso).getTime(), 0);
  if (diff < HOUR) return i18n.t("format.age.m", "{{n}}m", { n: Math.floor(diff / MINUTE) });
  if (diff < DAY) return i18n.t("format.age.h", "{{n}}h", { n: Math.floor(diff / HOUR) });
  return i18n.t("format.age.d", "{{n}}d", { n: Math.floor(diff / DAY) });
}

/** "4 min ago", "2 h ago", "3 d ago"; a future instant reads "in 4 min". */
export function relative(iso: string, now: Date = new Date()): string {
  const diff = now.getTime() - new Date(iso).getTime();
  const size = Math.abs(diff);
  if (isArabic()) {
    const unit = size < HOUR ? "minute" : size < DAY ? "hour" : "day";
    const unitMs = unit === "minute" ? MINUTE : unit === "hour" ? HOUR : DAY;
    // Intl reads a negative value as "ago" and a positive one as "in"; `diff` is now - then.
    return new Intl.RelativeTimeFormat(AR, { numeric: "auto" }).format(-Math.sign(diff) * Math.round(size / unitMs), unit);
  }
  const span =
    size < HOUR
      ? `${Math.round(size / MINUTE)} min`
      : size < DAY
        ? `${Math.round(size / HOUR)} h`
        : `${Math.round(size / DAY)} d`;
  return diff < 0 ? `in ${span}` : `${span} ago`;
}
